import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import useClickOutside from "@/hooks/useClickOutSide";

interface DurationInputProps {
  value?: { amount: number; unit: string };
  onChange: (value: { amount: number; unit: string }) => void;
  disabled?: boolean;
  className?: string;
  type?: "in_the_last" | "due_in";
}

const DurationInput: React.FC<DurationInputProps> = ({
  value,
  onChange,
  disabled = false,
  className = "",
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setShowDropdown(false));
  
  const timeUnits = [
    { label: "Days", value: "days" },
    { label: "Weeks", value: "weeks" },
    { label: "Months", value: "months" },
  ];

  const handleAmountChange = (amount: string) => {
    const numericAmount = parseInt(amount) || 0;
    onChange({
      amount: numericAmount,
      unit: value?.unit || "days"
    });
  };

  const handleUnitChange = (unit: string) => {
    onChange({
      amount: value?.amount || 0,
      unit: unit
    });
    setShowDropdown(false);
  };

  return (
    <div className={`relative w-full ${className}`}>
       <div className="flex gap-2 items-center">
          {/* Number Input */}
          <input
            type="number"
            min="1"
            value={value?.amount || ""}
            onChange={(e) => handleAmountChange(e.target.value)}
            placeholder="0"
            disabled={disabled}
            className={`
             w-full px-3 py-7 text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500
              ${disabled ? "cursor-not-allowed opacity-60" : ""}
              hover:border-gray-400 focus:border-text-primary-500
            `}
          />
          
          {/* Unit Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setShowDropdown(!showDropdown)}
              disabled={disabled}
              className={`
                flex items-center justify-between gap-2 px-3 py-7 text-text-default 
                rounded-lg outline-none border border-fieldBorder min-w-[80px]
                ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
                hover:border-gray-400 focus:border-text-primary-500
              `}
            >
              <span className="text-13">
                {timeUnits.find(unit => unit.value === value?.unit)?.label || "Days"}
              </span>
              <ChevronDown className={`size-4 text-gray-400 transition-transform ${showDropdown ? "rotate-180" : ""}`} />
            </button>
            
            {showDropdown && (
              <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                {timeUnits.map((unit) => (
                  <button
                    key={unit.value}
                    type="button"
                    onClick={() => handleUnitChange(unit.value)}
                    className={`
                      w-full px-3 py-2 text-left text-13 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg
                      ${value?.unit === unit.value ? "bg-blue-50 text-blue-600" : "text-gray-700"}
                    `}
                  >
                    {unit.label}
                    {value?.unit === unit.value && (
                      <span className="float-right text-blue-600">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default DurationInput;
